import ProjectCommissionReport from "nvent-web/scenes/ProjectCommissionReport";
import AddRoom from "nvent-web/scenes/AddRoom";
import { certifiedPageFinishedForLocale } from "nvent-web/scenes/CertifiedPro/utils/certifiedPageFinishedForLocale";
import CustomerSupportNumbers from "nvent-web/scenes/CustomerSupportNumbers/CustomerSupportNumbers";
import EditLevel from "nvent-web/scenes/EditLevel";
import EditProject from "nvent-web/scenes/EditProject";
import EditRoom from "nvent-web/scenes/EditRoom";
import { independentSelectionGuideRoutes } from "nvent-web/scenes/IndependentSelectionGuide/IndependentSelectionGuide";
import ProjectDetails from "nvent-web/scenes/ProjectDetails";
import FinishedProjects from "nvent-web/scenes/Projects/FinishedProjects";
import OpenProjects from "nvent-web/scenes/Projects/OpenProjects";
import RoomDetails from "nvent-web/scenes/RoomDetails";
import { selectionGuideRoutes } from "nvent-web/scenes/SelectionGuide/SelectionGuide";
import Tests from "nvent-web/scenes/Tests";
import UserSettings from "nvent-web/scenes/UserSettings";
import AppStore from "nvent-web/stores/App";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { UserStore } from "nvent-web/stores/User";
import { getAwaitingProject } from "nvent-web/utils/awaitingProject";
import { ContinueCreatingProject } from "nvent-web/scenes/ContinueCreatingProject/ContinueCreatingProject";

import { Route } from "react-router";
import Redirect from "~/nvent-web/components/Redirect";
import AuthenticatedAppLayout from "./AuthenticatedAppLayout";
import NewProject from "~/nvent-web/scenes/NewProject";
import { CertifiedProPage } from "~/nvent-web/scenes/CertifiedPro/CertifiedProPage";
import { NonCertifiedProPage } from "~/nvent-web/scenes/CertifiedPro/NonCertifiedProPage";

export interface AuthenticatedAppInnerProps {
  app: AppStore;
  notifications: NotificationsStore;
  user: UserStore;
}

export const authenticatedAppRoutes = ({
  notifications,
  user,
}: {
  app: AppStore;
  notifications: NotificationsStore;
  user: UserStore;
}) => {
  const awaitingProject = getAwaitingProject();

  const { isProfileComplete, certifiedProNumber, locale } = user;

  return (
    <Route
      path="/"
      element={<AuthenticatedAppLayout notifications={notifications} />}
    >
      <Route path="settings" Component={UserSettings} />
      <Route path="projects/open" Component={OpenProjects} />
      <Route path="projects/new" Component={NewProject} />
      <Route path="projects/continue" Component={ContinueCreatingProject} />
      <Route path="projects/finished" Component={FinishedProjects} />
      <Route path="projects/:projectId" Component={ProjectDetails} />
      <Route
        path="/projects/:projectId/commission-report"
        Component={ProjectCommissionReport}
      />
      <Route path="projects/:projectId/edit" Component={EditProject} />
      <Route path="projects/:projectId/levels/:id/edit" Component={EditLevel} />
      <Route path="projects/:projectId/rooms/new" Component={AddRoom} />
      <Route path="projects/:projectId/rooms/:roomId" Component={RoomDetails} />
      <Route
        path="projects/:projectId/rooms/:roomId/edit"
        Component={EditRoom}
      />
      <Route
        path="projects/:projectId/rooms/:roomId/room_products/:productId/tests"
        Component={Tests}
      />

      {certifiedPageFinishedForLocale(locale) && (
        <Route
          path="certifiedpro"
          Component={
            certifiedProNumber ? CertifiedProPage : NonCertifiedProPage
          }
        />
      )}
      <Route path="/customer-support" Component={CustomerSupportNumbers} />
      {selectionGuideRoutes()}
      {independentSelectionGuideRoutes({
        fullWidth: true,
      })}
      <Route
        index
        element={indexRedirects({
          isProfileComplete,
          isAwaitingProject: awaitingProject !== null,
        })}
      />
    </Route>
  );
};

const indexRedirects = ({
  isAwaitingProject,
  isProfileComplete,
}: {
  isAwaitingProject: boolean;
  isProfileComplete: boolean;
}) => {
  if (!isProfileComplete) {
    return <Redirect to="settings" />;
  }

  if (isAwaitingProject) {
    return <Redirect to="projects/continue" />;
  }

  return <Redirect to="projects/open" />;
};

authenticatedAppRoutes.displayName = "AuthenticatedAppRoutes";
